import type {
  AccountsReviewDetail,
  AdjustmentKind,
  AdjustmentMode,
  BillAge,
  CaseRelease,
  DeliverTo,
  PaymentStatus,
  ReviewCleanup,
  BulkOutcome,
  ItemsReceived,
  JobOrder,
  Kit,
  KitSpecification,
  NewSpan,
  Origin,
  ShippingMethod,
  Stage,
  UndoRef,
} from '@/domain';

/** Section 8.1. Intake and the order lifecycle. */

export interface CreateJobOrderCommand {
  /*
   * The doctor and the patient, each by the id of its record or by name. The
   * request of 6 October 2026 (second), items 4 and 5.
   *
   * A doctor or a patient chosen from the suggestions travels as its id, with
   * the name beside it; a name nobody has typed before travels alone and the
   * server keeps it as a new record. The server reads the id when there is
   * one.
   *
   * The two are chosen apart, since the request of 6 October 2026 (third),
   * item 3: a patient is one record whichever doctor they are seen by, so any
   * patient may be chosen whoever the doctor is, and changing the doctor
   * leaves a chosen patient chosen.
   */
  dentistName: string;
  patientName: string;
  doctorId?: string | null;
  patientId?: string | null;
  /**
   * True, beside a typed `patientName` and no `patientId`, to make a new
   * patient of that name although somebody already has it: a different person
   * of the same name. Left off, the name means whoever already has it.
   */
  newPatient?: boolean;
  /**
   * The number on the paper job order handed to the doctor: optional, up to 32
   * characters. On an edit, null takes it off. The lab's request of
   * 17 October 2026, item 7.
   */
  serialNumber?: string | null;
  /** Local, international or online, in place of the country. Section 1 of the request of 24 September 2026. */
  origin: Origin;
  input: 'digital' | 'impression';
  /**
   * The case's headline item.
   *
   * What the order lists show and what the production route is chosen from. It
   * is also the fallback for a tooth that names nothing of its own, which is
   * every tooth on a case where the whole tray is one item.
   */
  product: string;
  /**
   * The chart, each tooth with its own item.
   *
   * May be empty: a case can be opened on `units` alone, because reception
   * takes work over the phone knowing there are four crowns before knowing
   * which four. Omit `product` on a tooth to take the case's.
   */
  teeth: { tooth: string; product?: string }[];
  /** How many teeth the case covers. Never below `teeth.length`. */
  units: number;
  /**
   * The bridges and titanium bars. Every tooth each one covers must be in
   * `teeth`; the rules and their wording are in `domain/teeth/spans`.
   */
  spans: NewSpan[];
  shade: string;
  /** The lab's paper specification, filled in by reception. */
  specification: KitSpecification;
  /** What physically arrived with the case. */
  itemsReceived: ItemsReceived;
  startDate: string;
  /** Only accepted from a caller holding `editDates`; the server recalculates otherwise. */
  deliveryDate?: string;
  /** The instructions on the case: one field, since the request of 5 October 2026. */
  instructions: string;
  /**
   * A remake, booked in at intake: the case this one remakes, and why it came
   * back. Requires `redo`. `redoOf` is a job order from the archive, or, with
   * `redoOfLegacy`, the old number of a case from before this system, typed by
   * hand: one to thirty two characters. The request of 6 October 2026
   * (second), item 1. Sent only when the case is a remake.
   */
  redoOf?: string | null;
  redoReason?: string | null;
  redoOfLegacy?: boolean;
  /** Urgent from the moment it is booked in. Ignored without `markUrgent`. */
  urgent?: boolean;
  /**
   * The bench the case opens at. Section 4 of the request of 20 September 2026.
   *
   * Omitted means the first stage after Intake on the case's own route, which is
   * what the form preselects. `ready` and `finalcheck` are refused with a 400
   * naming the field.
   */
  startStage?: Stage;
}

/**
 * `PATCH /job-orders/{jo}`: editing a case after intake. Only the fields that
 * change travel, and the rowVersion goes as If-Match. System test handover 2.4.
 *
 * The doctor and the patient change by id or by name, as at intake, with
 * `newPatient` beside a typed patient's name for a second record of it. What
 * is not sent is kept: a case whose doctor changes keeps its patient. What a
 * case remakes and its urgency are not edits to its contents, and do not
 * travel here.
 */
export interface UpdateJobOrderCommand {
  jobOrderNo: string;
  rowVersion: string;
  changes: Partial<
    Omit<
      CreateJobOrderCommand,
      'startDate' | 'deliveryDate' | 'redoOf' | 'redoReason' | 'redoOfLegacy' | 'urgent'
    >
  >;
}

export interface SetDatesCommand {
  jobOrderNo: string;
  startDate: string;
  deliveryDate: string;
  rowVersion: string;
}

export interface JobOrderSummary {
  jobOrderNo: string;
  state: string;
  kitCount: number;
  deliveryDate: string;
}

export interface CreateJobOrderResult {
  jobOrder: JobOrderSummary;
  /** One per tooth for a unit product, one for a span. The server decides. */
  kits: Kit[];
}

/**
 * A case as an edit saved it, with the undo of an edit that changed the input
 * type or an item: the lab's request of 18 October 2026. Null or absent for
 * any other edit.
 */
export interface UpdateJobOrderResult extends CreateJobOrderResult {
  undo?: UndoRef | null;
}

export interface CatalogItemView {
  /** What a case keeps: a rename changes the name and never the id. */
  id: string;
  name: string;
  category: string;
  unitPrice?: number | null;
  kind: 'unit' | 'span';
  /** False once retired. Still listed, so an old case can show its name. */
  active: boolean;
  rowVersion: string;
}

/** What the Orders list may be narrowed to by its due date. */
export const ORDER_DUES = ['today', 'overdue'] as const;
export type OrderDue = (typeof ORDER_DUES)[number];

export interface JobOrderFilter {
  state?: string;
  urgent?: boolean;
  origin?: Origin;
  dentist?: string;
  includeDispatched?: boolean;
  /**
   * Due today, or overdue, among the cases in hand, by the server's one rule:
   * exactly the cases the Overview's Due and Overdue figures count. The
   * request of 7 October 2026, sections 6 and 7.
   */
  due?: OrderDue;
  /** Leave out an order delivered before this lab day (YYYY-MM-DD). */
  deliveredSince?: string;
  /**
   * Accounts' lists: to be paid, paid, changed since it was priced, or reviewed
   * (its changes marked reviewed and nothing waiting, newest review first).
   * Released (shipped unpaid with Accounts' leave, and still not paid) and
   * In ceramic (a kit at Ceramic now) since the lab's request of
   * 17 October 2026, items 8 and 14.
   */
  money?: 'unpaid' | 'part' | 'paid' | 'changed' | 'reviewed' | 'released' | 'ceramic';
  /** With `money`: the cases received that long ago, as the overview's ages count them. Item 1 of the same request. */
  age?: BillAge;
  /**
   * The cases still being made: in the lab and not every kit Ready, so neither
   * Ready nor Logistics. Item 3 of the same request: what Active counts.
   */
  active?: boolean;
  /** One of the statuses the Status column reads, asked of the server. Item 5 of the same request. */
  status?: string;
  page?: number;
  pageSize?: number;
  q?: string;
  /**
   * A named order the server sorts the whole list by: the origin sorts,
   * `localFirst`, `internationalFirst` and `onlineFirst`. Section 2 of the
   * request of 24 September 2026. Omitted, urgent first then soonest due.
   */
  sort?: string;
}

/**
 * The cases in hand, counted, each by the condition the list is narrowed by.
 * The admin's figures on the Production line and Orders: the lab's request of
 * 17 October 2026, items 3 and 5.
 */
export interface JobOrderCounts {
  inHand: number;
  active: number;
  dueToday: number;
  overdue: number;
  urgent: number;
  held: number;
}

export interface JobOrderPage {
  items: JobOrder[];
  page: number;
  pageSize: number;
  total: number;
}

/** Every deletion command names the case and sends the rowVersion it was read at, as If-Match. */
export interface DeletionCommand {
  jobOrderNo: string;
  rowVersion: string;
}

/**
 * The case leaves the lab. A courier case names its courier and may carry the
 * waybill code, typed by hand and never generated, or leave it for the morning;
 * a lab delivery carries no code and may name its driver.
 */
export interface DispatchCommand {
  jobOrderNo: string;
  method: ShippingMethod;
  carrier?: string;
  /** The courier's name, when `carrier` is `Other`. The server resolves the two into one. */
  carrierOther?: string;
  trackingCode?: string;
  driverName?: string;
  note?: string;
  /** Left off for now. A day chosen before today, as the instant it stands for. */
  dispatchedAt?: string;
}

/** What an edit to a case's shipping changes. Only what changes travels; null clears. */
export interface ShippingChanges {
  /** How it left, corrected: the lab's request of 22 October 2026. A courier is named with it. */
  method?: ShippingMethod;
  carrier?: string;
  /** The name typed beside `Other`. The server resolves the two into one. */
  carrierOther?: string;
  trackingCode?: string | null;
  driverName?: string | null;
  note?: string | null;
  dispatchedAt?: string;
  trackedAt?: string | null;
  deliveredAt?: string | null;
  receivedBy?: string | null;
}

/** Adding the waybill code or correcting any of it, with the order's rowVersion as If-Match. */
export interface UpdateShippingCommand {
  jobOrderNo: string;
  rowVersion: string;
  changes: ShippingChanges;
}

/** Marking a case received by the customer, with the order's rowVersion as If-Match. */
export interface MarkDeliveredCommand {
  jobOrderNo: string;
  rowVersion: string;
  /** Left off for now. */
  deliveredAt?: string;
  receivedBy?: string;
}

/**
 * Taking a received mark back: the Undo offered after Mark received. The
 * rowVersion is the one the marking answered with, so the server refuses once
 * anybody has changed the case since. Sent as an edit of the shipping with
 * `deliveredAt` null; there is no call of its own. The request of
 * 5 October 2026 (third), section 11.
 */
export interface UnmarkReceivedCommand {
  jobOrderNo: string;
  rowVersion: string;
}

/** One line of a case's receipt. Request of 5 October 2026. */
export interface ReceiptLine {
  key: string;
  kitId: string;
  product: string;
  category: string | null;
  teeth: string[];
  units: number;
  unitPrice: number;
  perSpan: boolean;
  value: number;
  adjustedFrom: number | null;
}

/**
 * What an invoice is headed with: Mark paid shows a case as one. The lab's
 * request of 17 October 2026, item 12. The names and where it goes only for a
 * reader who may see them.
 */
export interface ReceiptInvoice {
  serialNumber: string | null;
  receivedAt: string;
  deliveryDate: string;
  doctorCode: string | null;
  release: CaseRelease | null;
  dispatched: boolean;
  doctorName?: string | null;
  patientName?: string | null;
  deliverTo?: DeliverTo | null;
}

export interface Receipt {
  jobOrderNo: string;
  chargeable: boolean;
  paid: boolean;
  paidAt: string | null;
  lines: ReceiptLine[];
  /** What the lines come to. */
  total: number;
  /*
   * The request of 8 October 2026 (fourth): the discounts and charges on the
   * case, what it owes after them, what has been paid and what is left.
   * Absent from an API older than they are.
   */
  adjustments?: {
    id: string;
    kind: AdjustmentKind;
    mode: AdjustmentMode;
    value: number;
    amount: number;
    reason: string;
  }[];
  due?: number;
  paidAmount?: number;
  balance?: number;
  status?: PaymentStatus;
  /** Absent from an API older than invoices. */
  invoice?: ReceiptInvoice;
}

export interface SaveReceiptCommand {
  jobOrderNo: string;
  lines: { key: string; unitPrice: number }[];
}

/**
 * Marking a case's changes reviewed. `seen` is the `since` of the changes the
 * screen showed, so a case edited again in the meantime is refused rather than
 * reviewed unread; left off, or null, by a caller that showed none. The request
 * of 5 October 2026 (third), section 3.
 */
export interface MarkReviewedCommand {
  jobOrderNo: string;
  seen?: string | null;
}

export interface JobOrderRepository {
  list(filter: JobOrderFilter): Promise<JobOrderPage>;
  byNo(jobOrderNo: string): Promise<{ jobOrder: JobOrder; kits: Kit[] }>;
  /**
   * `requestId` travels as the Idempotency-Key. One per intake form, so pressing
   * Create again after a lost answer gets the case already made, not a second.
   */
  create(command: CreateJobOrderCommand, requestId?: string): Promise<CreateJobOrderResult>;
  catalog(): Promise<{ items: CatalogItemView[]; categories: string[] }>;

  /** The cases in hand, counted. Anybody signed in. */
  counts(): Promise<JobOrderCounts>;

  markPaid(jobOrderNo: string): Promise<JobOrder>;
  /** Takes a payment back: the Undo after Mark paid. Requires `pay`. */
  undoPayment(jobOrderNo: string): Promise<JobOrder>;
  /**
   * Releases the case to be shipped before it is paid. Requires `pay`. The
   * lab's request of 17 October 2026, item 8.
   */
  release(jobOrderNo: string): Promise<JobOrder>;
  /** Takes a release back, while the case is still in the lab: its Undo, and later. */
  takeBackRelease(jobOrderNo: string): Promise<JobOrder>;
  /** The case's receipt: every line, its teeth, its price and the total. Requires `pay`. */
  receipt(jobOrderNo: string): Promise<Receipt>;
  /** New prices for this case only; the price list is untouched. 409 once paid. */
  saveReceipt(command: SaveReceiptCommand): Promise<Receipt>;
  /**
   * What there is to review on a case, each change with who made it, when, and
   * every field before and after, and the review that stands on it. Requires
   * `pay`. The request of 5 October 2026 (third), section 3.
   */
  accountsReviewDetail(jobOrderNo: string): Promise<AccountsReviewDetail>;
  /**
   * The cases waiting for Accounts that changed nothing of the bill, for
   * management to read before clearing them. The request of 7 October 2026, section 5.
   */
  reviewCleanup(): Promise<ReviewCleanup>;
  /** Clears them. Answers how many cases left the Changed list. */
  clearReviewCleanup(): Promise<number>;
  /**
   * Accounts have looked at the edits since the case was priced. `seen` is the
   * detail's `since`, sent by a screen that showed the change: the server
   * refuses with 409 when the case was edited again after that.
   */
  accountsReviewed(jobOrderNo: string, seen?: string): Promise<JobOrder>;
  /**
   * Takes a review back: the Undo after Mark as reviewed, and Mark as not
   * reviewed later. Requires `pay`; a case with no review standing is answered
   * as it is.
   */
  accountsUnreview(jobOrderNo: string): Promise<JobOrder>;
  /**
   * Requires `editDates`. Both dates travel, and the rowVersion goes as
   * If-Match so two people editing the same order get a 409 rather than one
   * silently overwriting the other.
   */
  setDates(command: SetDatesCommand): Promise<JobOrder>;
  /** Requires `editCase`. Answers with the order and its kits. */
  updateJobOrder(command: UpdateJobOrderCommand): Promise<UpdateJobOrderResult>;
  hold(jobOrderNo: string, reason: string): Promise<JobOrder>;
  releaseHold(jobOrderNo: string): Promise<JobOrder>;
  dispatch(command: DispatchCommand): Promise<JobOrder>;
  /** Requires `ship`. */
  updateShipping(command: UpdateShippingCommand): Promise<JobOrder>;
  /** Requires `ship`. */
  markDelivered(command: MarkDeliveredCommand): Promise<JobOrder>;

  /**
   * Requires `deleteCase`. Deletes the case for a holder of `approveDeletion`,
   * and asks for it for anybody else; the answer says which happened.
   */
  requestDeletion(command: DeletionCommand & { reason: string }): Promise<JobOrder>;
  /** Withdraws your own request, or refuses anybody's with `approveDeletion`. */
  withdrawDeletion(command: DeletionCommand): Promise<JobOrder>;
  /** Requires `approveDeletion`, and somebody else's request. */
  approveDeletion(command: DeletionCommand): Promise<JobOrder>;
  /** Requires `approveDeletion`. Brings a deleted case back exactly as it was. */
  restoreCase(command: DeletionCommand): Promise<JobOrder>;
  /**
   * Requires `approveDeletion`, and a deleted case. Takes it out of the system
   * now rather than on its purge day, and nothing brings it back. Request of
   * 24 September 2026.
   */
  purgeCase(command: DeletionCommand): Promise<void>;

  /*
   * The commands on a selection of cases, section 4 of the request of
   * 24 September 2026. Each answers one outcome per case, so a case that could
   * not be moved does not stop the rest, and the screen says which and why.
   */
  /** Requires `archiveCase`. Off every list of work in hand, into the Archived tab. */
  archiveMany(jobOrderNos: string[], reason?: string): Promise<BulkResult>;
  /** Requires `archiveCase`. Back exactly as they went in. */
  unarchiveMany(jobOrderNos: string[]): Promise<BulkResult>;
  /** Requires `deleteCase`. Each as `requestDeletion` would, with one reason for all. */
  deleteMany(jobOrderNos: string[], reason: string): Promise<BulkResult>;
  /*
   * The money actions on many cases at once, and the Undo of each: the lab's
   * request of 17 October 2026, items 9 and 10. All require `pay`.
   */
  payMany(jobOrderNos: string[]): Promise<BulkResult>;
  unpayMany(jobOrderNos: string[]): Promise<BulkResult>;
  releaseMany(jobOrderNos: string[]): Promise<BulkResult>;
  unreleaseMany(jobOrderNos: string[]): Promise<BulkResult>;
}

/** The answer to a command on a selection: one outcome per case, and the two counts. */
export interface BulkResult {
  results: BulkOutcome[];
  done: number;
  refused: number;
  /**
   * `assignMany` only: one undo for the whole selection, for the person who
   * sent it and for a moment. Null, or absent, when nothing changed. Section 6
   * of the request of 5 October 2026.
   */
  undo?: UndoRef | null;
}
